import { useState } from "react";
import { Mail, X, Loader2 } from "lucide-react";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";

export function VerificationBanner() {
  const {
    hasAccount,
    isEmailVerified,
    verificationBannerDismissed,
    userEmail,
    authToken,
    dismissVerificationBanner,
  } = useAppStore();
  const { toast } = useToast();
  const [resending, setResending] = useState(false);

  if (!hasAccount || isEmailVerified || verificationBannerDismissed) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/auth/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (res.ok) {
        toast({ title: "Verification email sent!", description: "Check your inbox." });
      } else {
        toast({ title: "Failed to resend", description: "Please try again later.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex items-center gap-3 mb-4">
      <Mail className="w-4 h-4 text-amber-400 shrink-0" />
      <p className="text-sm text-amber-200/80 flex-1 min-w-0">
        <span className="hidden sm:inline">Check your inbox — we sent a verification link to </span>
        <span className="sm:hidden">Verify your email: </span>
        <span className="font-medium text-amber-100">{userEmail}</span>
      </p>
      <button
        onClick={handleResend}
        disabled={resending}
        className="text-xs font-medium text-amber-300 hover:text-amber-100 transition-colors shrink-0"
      >
        {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Resend"}
      </button>
      <button
        onClick={dismissVerificationBanner}
        aria-label="Dismiss verification banner"
        className="text-amber-400/50 hover:text-amber-300 transition-colors shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
